/**
 * DATEV-Export für den Steuerberater.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { today } from '@erp/shared';

import { api, withQuery } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Alert, Field, LoadingBlock, TextInput } from '../components/ui';

function monthStart(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

export function DatevPage() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['datev-preview', from, to],
    queryFn: () =>
      api.get<{
        entryCount: number;
        lineCount: number;
        warnings: string[];
        fileName: string;
        sizeBytes: number;
      }>(withQuery('/api/accounting/datev/preview', { from, to })),
    enabled: Boolean(from && to),
  });

  return (
    <>
      <PageHeader
        title="DATEV-Export"
        subtitle="Buchungsstapel im Format EXTF für die Steuerkanzlei"
      />

      <Alert kind="info">
        Der Export enthält alle Buchungen des gewählten Zeitraums als CSV-Datei im
        DATEV-Format (Windows-1252, Semikolon getrennt). Die Datei wird <strong>nicht</strong> als
        festgeschrieben gekennzeichnet — das entscheidet die Kanzlei beim Einlesen.
      </Alert>

      <div className="card">
        <div className="card-body">
          <div className="form-grid">
            <Field label="Von" span={4} required>
              <TextInput type="date" value={from} onChange={setFrom} />
            </Field>
            <Field label="Bis" span={4} required>
              <TextInput type="date" value={to} onChange={setTo} />
            </Field>
          </div>

          {isLoading ? <LoadingBlock text="Zeitraum wird geprüft …" /> : null}

          {isError ? <Alert kind="error">{(error as Error).message}</Alert> : null}

          {data ? (
            <>
              {data.warnings.map((warning) => (
                <Alert kind="warning" key={warning}>
                  {warning}
                </Alert>
              ))}

              {data.entryCount === 0 ? (
                <Alert kind="warning">In diesem Zeitraum gibt es keine Buchungen.</Alert>
              ) : (
                <>
                  <dl className="definition-list" style={{ marginTop: 16 }}>
                    <dt>Buchungssätze</dt>
                    <dd>{data.entryCount}</dd>
                    <dt>Zeilen im Stapel</dt>
                    <dd>{data.lineCount}</dd>
                    <dt>Dateiname</dt>
                    <dd className="mono">{data.fileName}</dd>
                    <dt>Größe</dt>
                    <dd>{(data.sizeBytes / 1024).toFixed(1)} KB</dd>
                  </dl>

                  <div style={{ marginTop: 18 }}>
                    <a
                      className="btn btn-primary"
                      href={withQuery('/api/accounting/datev/export', { from, to })}
                    >
                      Buchungsstapel herunterladen
                    </a>
                  </div>

                  <p className="faint" style={{ fontSize: 12.5, marginTop: 12 }}>
                    Nach dem Herunterladen werden die enthaltenen Buchungen als „exportiert“
                    markiert. Sie bleiben weiterhin sichtbar und können erneut exportiert werden.
                  </p>
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
